import type { FC, RefObject } from "react";
import React from "react";
import { noop } from "@litres/utils";
import classnames from "classnames";
import { useEventListener } from "@litres-libs/global-hooks";
import { useTranslation } from "@litres-libs/i18n";
import { CloseIcon } from "../../../../static/icons/CloseIcon";
import type { ISearchResultItem } from "../types";
import { useRefCallback } from "../../../hooks/useRefCallback";
import { KeyCodes } from "../constants";
import { isFocusElement } from "../../../utils/domHelpers";

import styles from "./SearchResult.module.scss";

export interface SearchResultProps {
  items: ISearchResultItem[];
  isUserAnonymous: boolean;
  isDesktop?: boolean;
  onItemClick?: (item: ISearchResultItem, index?: number) => void;
  onItemDeleteClick: (value: string) => void;
  inputRef?: RefObject<HTMLInputElement>;
}

export const SearchResult: FC<SearchResultProps> = ({
  items,
  onItemDeleteClick,
  onItemClick = noop,
  isDesktop = true,
  inputRef,
}) => {
  const { t } = useTranslation();
  const [searchResultListNode, searchResultRefCallback] =
    useRefCallback<HTMLUListElement>();

  const handleResultItemClick = (item: ISearchResultItem, index?: number) => {
    onItemClick(item, index);
    window.location.assign(`/search/?q=${encodeURIComponent(item.value)}`);
  };

  const handleKeyPress = (event: Event): void => {
    if (!searchResultListNode) {
      return;
    }

    const { key } = event as KeyboardEvent;

    const menuItems = searchResultListNode
      ? Array.from(searchResultListNode.children)
      : [];

    if (key === KeyCodes.Enter && menuItems.some(isFocusElement)) {
      handleResultItemClick(
        items[menuItems.findIndex(isFocusElement)],
        menuItems.findIndex(isFocusElement)
      );
      return;
    }

    const firstIndex = 0;
    const lastIndex = menuItems.length - 1;
    const currentIndex = menuItems.findIndex(isFocusElement);

    const prevIndex = isFocusElement(menuItems[firstIndex])
      ? lastIndex
      : currentIndex - 1;

    const nextIndex = isFocusElement(menuItems[lastIndex])
      ? firstIndex
      : currentIndex + 1;

    if (key === KeyCodes.ArrowDown) {
      (
        menuItems[currentIndex === -1 ? firstIndex : nextIndex] as HTMLElement
      ).focus();
    }

    if (key === KeyCodes.ArrowUp) {
      if (
        inputRef?.current &&
        (currentIndex === -1 || prevIndex === lastIndex)
      ) {
        inputRef.current.focus();
      } else {
        (
          menuItems[currentIndex === -1 ? lastIndex : prevIndex] as HTMLElement
        ).focus();
      }
    }
  };

  const handleClearClick = (value: string) => (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onItemDeleteClick(value);
  };

  const handleSearchResultTouchStart = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  useEventListener("keydown", handleKeyPress);

  return (
    <ul
      className={classnames(styles.searchResultList, {
        [styles.mobileList]: !isDesktop,
      })}
      ref={searchResultRefCallback}
      onTouchStart={handleSearchResultTouchStart}
      data-testid="header__search-result"
      role="listbox"
      aria-label={t("Подсказки поиска")}
      id="suggestionsSearchResult"
    >
      {items.map((item, index) => (
        <li
          role="option"
          className={classnames(styles.item, {
            [styles.item_mobile]: !isDesktop,
          })}
          key={`${item.value}${item.type}`}
          tabIndex={0}
          onClick={(): void => {
            handleResultItemClick(item, index);
          }}
          onKeyDown={e => {
            if (e.key === "Enter") {
              handleResultItemClick(item, index);
            }
          }}
          data-testid={
            item.type === "suggestion"
              ? "search__saggestion--wrapper"
              : "search__history--wrapper"
          }
        >
          <span className={styles.itemText}>{item.value}</span>
          {item.type === "history" && (
            <button
              tabIndex={0}
              aria-label={t("Убрать из истории поиска")}
              className={styles.closeIcon}
              onClick={handleClearClick(item.value)}
              onKeyDown={e => {
                if (e.key === "Enter") {
                  e.stopPropagation();
                  e.preventDefault();
                  onItemDeleteClick(item.value);
                }
              }}
            >
              <CloseIcon />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
};
